import { valibotResolver } from "@hookform/resolvers/valibot";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import type { OutreachSourceType } from "features/integrations/types";

import Loader from "common/components/Loader";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useCrmLeads from "../../../datahooks/useCrmLeads";
import { CrmSourceSchema, type CrmSourceSchemaType } from "../../../schemas";
import CsvOverview from "../CsvOverview";
import ScrollAndFooter from "../ScrollAndFooter";
import SourceFormField from "./SourceFormField";
import SourceTypeFormField from "./SourceTypeFormField";

import type { CrmLeadListSource } from "../../../types";

import CrmImage from "assets/images/sources/crm.png";

interface ConnectCrmProps {
  createSource: (params: {
    settings: CrmLeadListSource["settings"];
    crm_type: OutreachSourceType;
  }) => void;
  isCreatingSource: boolean;
}

export default function ConnectCrm({
  createSource,
  isCreatingSource,
}: ConnectCrmProps) {
  const isDesktop = useTwBreakpoint("lg");

  const {
    watch,
    setValue,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
    control,
    resetField,
  } = useForm<CrmSourceSchemaType>({
    resolver: valibotResolver(CrmSourceSchema),
  });

  const sourceType = watch("sourceType");
  const sourceId = watch("sourceId");
  const mainIdentifier = watch("mainIdentifier");

  const { crmLeadsState, isLoadingCrmLeadsState } = useCrmLeads(
    sourceType,
    sourceId,
    mainIdentifier,
  );

  function resetState() {
    resetField("mainIdentifier");
    resetField("sourceId");
  }

  useEffect(() => {
    if (crmLeadsState) {
      const { leads, state } = crmLeadsState;

      if (state === "success" && leads.url_columns.length) {
        setValue("mainIdentifier", leads.url_columns[0]);
      }
    }
  }, [crmLeadsState, setValue]);

  useEffect(() => {
    resetState();
  }, [sourceType]);

  useEffect(() => {
    setValue("mainIdentifier", "");
  }, [sourceId, setValue]);

  function onSave() {
    createSource({
      settings: {
        type: "crm",
        profiles: crmLeadsState.leads.parsed,
        url_column: getValues("mainIdentifier"),
      },
      crm_type: sourceType,
    });
  }

  const isLoadingLeads =
    isLoadingCrmLeadsState || crmLeadsState?.state !== "success";

  let leadsComponent = (
    <img src={CrmImage} alt="" className="max-lg:order-first" height={238} />
  );

  if (sourceId) {
    leadsComponent = isLoadingLeads ? (
      <div className="flex px-6 py-14 mb-5 flex-col items-center rounded-3xl border border-black-300 bg-black-50">
        <Loader iconSize={32} />
        <p className="text-center text-button-md mt-3 mb-2.5">
          Reading your data
        </p>
        <p className="text-center text-xs-medium text-black-400">
          We're importing your contacts and matching up your columns
        </p>
      </div>
    ) : (
      <CsvOverview
        uploadState={{
          csvState: crmLeadsState.leads,
          sourceName: sourceType,
        }}
        mainIdentifier={mainIdentifier}
        setMainIdentifier={(identifier) =>
          setValue("mainIdentifier", identifier)
        }
        reset={() => {
          resetState();
        }}
      />
    );
  }

  return (
    <ScrollAndFooter
      isCreateDisabled={isLoadingLeads || !mainIdentifier}
      isCreatingSource={isCreatingSource}
      //validates the crm fields before saving, the same way the dialog flow
      //validated them before moving on to the leads step
      onCreate={handleSubmit(onSave)}
    >
      <div className="flex flex-col gap-y-3 lg:gap-y-5 max-w-135 w-full m-auto">
        {!sourceId && (
          <div className="flex flex-col gap-y-2">
            <div className="flex gap-x-2">
              <SourceTypeFormField control={control} />
              <SourceFormField control={control} />
            </div>
            {sourceType === "emailbison" && (
              <FormField
                htmlFor="linkedin-url-variable-name"
                label="LinkedIn URL variable name"
                error={errors.mainIdentifier?.message}
                className="flex-1"
              >
                <Input
                  variant={isDesktop ? "md" : "lg"}
                  placeholder="e.g. linkedin_url"
                  id="linkedin-url-variable-name"
                  {...register("mainIdentifier")}
                />
              </FormField>
            )}
          </div>
        )}

        {leadsComponent}
      </div>
    </ScrollAndFooter>
  );
}
